import { useCallback, useEffect, useState } from "react";
import { api, notifyError } from "@/lib/api";
import { Plus, Trash, FileArrowDown, Warning } from "@phosphor-icons/react";
import { downloadCsv } from "./PreTradingTab";
import RuleNote from "./RuleNote";

const RATES = [
  { id: "five_hour", label: "5+ hours (£5)" },
  { id: "ten_hour", label: "10+ hours (£10)" },
  { id: "late_evening", label: "Ongoing past 8pm (+£15)" },
];

export default function UkSubsistenceTab({ onChange }) {
  const [data, setData] = useState({ items: [], total_gbp: 0, daily_max: 25, unreported_count: 0 });
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), description: "", mode: "calculated", rate_key: "five_hour", count: "1", amount: "", reported_hmrc: false });

  const load = useCallback(async () => { const r = await api.get("/compliance/uk-subsistence"); setData(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { date: form.date, description: form.description, mode: form.mode, reported_hmrc: form.reported_hmrc };
    if (form.mode === "calculated") { payload.rate_key = form.rate_key; payload.count = Number(form.count || 1); }
    else { payload.amount = Number(form.amount || 0); }
    try {
      await api.post("/compliance/uk-subsistence", payload);
      setForm({ ...form, description: "", amount: "", count: "1" });
      load(); onChange && onChange();
    } catch (err) { notifyError(err); }
  };

  const toggle = async (id) => { await api.put(`/compliance/uk-subsistence/${id}/reported`); load(); onChange && onChange(); };
  const del = async (id) => { await api.delete(`/compliance/uk-subsistence/${id}`); load(); onChange && onChange(); };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6" data-testid="uk-subsistence-tab">
        <div className="flex items-center gap-2 eyebrow mb-1">
          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: "#5B8DEF22", color: "#5B8DEF", border: "1px solid #5B8DEF" }}>UK</span>
          Subsistence · HMRC benchmark scale rates
        </div>
        <div className="text-xs text-white/45 mb-4">Tax-free meal allowances without receipts. Max £{Number(data.daily_max).toFixed(0)} per 24-hour period. Or record a receipted amount.</div>

        <div className="flex gap-2 mb-4">
          {["calculated", "receipted"].map((m) => (
            <button key={m} type="button" data-testid={`uksub-mode-${m}`} onClick={() => setForm({ ...form, mode: m })}
              className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${form.mode === m ? "bg-[#5B8DEF]/15 text-white border border-[#5B8DEF]" : "text-white/55 border border-white/8 hover:border-white/20"}`}>
              {m === "calculated" ? "Benchmark rate" : "Receipted amount"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div><label>Date</label><input required type="date" data-testid="uksub-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div className="md:col-span-2"><label>Description</label><input data-testid="uksub-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Overnight in Manchester" /></div>
          {form.mode === "calculated" ? (
            <>
              <div className="md:col-span-2"><label>Rate</label><select data-testid="uksub-rate" value={form.rate_key} onChange={(e) => setForm({ ...form, rate_key: e.target.value })}>{RATES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select></div>
              <div><label>Count</label><input type="number" min="1" step="1" data-testid="uksub-count" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} /></div>
            </>
          ) : (
            <div className="md:col-span-3"><label>Amount (£)</label><input required type="number" step="0.01" data-testid="uksub-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
          )}
          <button type="submit" data-testid="uksub-add" className="btn-gold"><Plus weight="bold" size={14} /> Add</button>
        </form>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm"><span className="text-white/50">Total logged:</span> <span className="mono text-[#FFB020]" data-testid="uksub-total">£{Number(data.total_gbp).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
        <button onClick={() => downloadCsv("/compliance/uk-subsistence.csv", "uk-subsistence-log.csv")} data-testid="uksub-export" className="btn-ghost text-sm"><FileArrowDown size={16} /> Export CSV</button>
      </div>

      <div className="glass rounded-xl overflow-auto">
        <table className="data">
          <thead><tr><th>Date</th><th>Description</th><th>Basis</th><th className="text-right">Amount</th><th>HMRC</th><th /></tr></thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan="6" className="text-center text-white/40 py-8">No subsistence logged.</td></tr>
            ) : data.items.map((i) => (
              <tr key={i.entry_id} data-testid={`uksub-row-${i.entry_id}`}>
                <td className="mono text-xs">{i.date}</td>
                <td>{i.description || "—"}</td>
                <td className="text-white/70 text-xs">{i.mode === "calculated" ? `${(RATES.find((r) => r.id === i.rate_key) || {}).label || i.rate_key}${i.count > 1 ? ` ×${i.count}` : ""}` : "Receipted"}</td>
                <td className="num text-[#FFB020]">£{Number(i.amount).toFixed(2)}{i.over_daily_max ? <Warning size={13} weight="fill" className="inline ml-1 text-[#E63946]" /> : null}</td>
                <td>
                  <button onClick={() => toggle(i.entry_id)} data-testid={`uksub-hmrc-${i.entry_id}`} className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${i.reported_hmrc ? "badge-pass" : "badge-warn"}`}>{i.reported_hmrc ? "Reported" : "Pending"}</button>
                </td>
                <td><button onClick={() => del(i.entry_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`uksub-del-${i.entry_id}`}><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RuleNote id="uk_subsistence" />
    </div>
  );
}
